import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Input } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, take, takeUntil } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { ShActionItemComponent } from '../../../../elements/components/action-item/action-item.component';
import {
    ActionItemStyle,
    ButtonActionItem
} from '../../../../elements/components/action-item/entities/action-item.entity';
import { IconSize } from '../../../../elements/components/icon/entities/icon-sizes.entity';
import { ShIconComponent } from '../../../../elements/components/icon/icon.component';
import {
    AutocompleteFormSelectOption,
    FormSelectOption
} from '../../../../form-controls/entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../../form-controls/entities/form-value.entity';
import { BaseControlValueAccessor, MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { splitFilter } from '../../../../helpers/split-filter-helper';
import { FormDualListI18n } from '../entities/form-dual-list-i18n';

@Component({
    selector: 'sh-form-dual-list-base',
    templateUrl: './form-dual-list-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormDualListBaseComponent)],
    standalone: true,
    imports: [ReactiveFormsModule, ShIconComponent, ShActionItemComponent, AsyncPipe]
})
export class ShFormDualListBaseComponent extends BaseControlValueAccessor {
    public sizes: typeof IconSize = IconSize;
    public leftSearchControl = new UntypedFormControl();
    public rightSearchControl = new UntypedFormControl();

    public leftLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public rightLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public moveActionItems$!: Observable<ButtonActionItem[]>;

    private readonly initialSelectedOptions$: ReplaySubject<FormSelectOption[]> = new ReplaySubject<
        FormSelectOption[]
    >();
    public allLeftItems$: BehaviorSubject<AutocompleteFormSelectOption[]> = new BehaviorSubject<
        AutocompleteFormSelectOption[]
    >([]);

    public leftItems$!: Observable<AutocompleteFormSelectOption[]>;
    public rightItems$: BehaviorSubject<AutocompleteFormSelectOption[]> = new BehaviorSubject<
        AutocompleteFormSelectOption[]
    >([]);

    public filteredLeftItems$!: Observable<AutocompleteFormSelectOption[]>;
    public filteredRightItems$!: Observable<AutocompleteFormSelectOption[]>;
    public state$: BehaviorSubject<FormValue<string>[]> = new BehaviorSubject<FormValue<string>[]>([]);

    private lastLeftIndex = -1;
    private lastRightIndex = -1;
    private shiftPressed = false;
    private isSingleClick = true;

    @Input() id!: string;
    @Input() set allOptions(allOptions: FormSelectOption[]) {
        this.allLeftItems$.next(allOptions.map(option => ({ ...option, checked: false })));
    }

    @Input() set initialSelectedOptions(initialSelectedOptions: FormSelectOption[]) {
        this.initialSelectedOptions$.next(initialSelectedOptions);
    }

    @Input() i18n!: FormDualListI18n;
    @Input() leftHeaderText?: string;
    @Input() rightHeaderText?: string;
    @Input() leftHasError?: boolean = false;
    @Input() rightHasError?: boolean = false;
    @Input() disabledMsg?: string = '';

    @Input() set leftLoading(loading: boolean) {
        this.leftLoading$.next(loading);
    }

    @Input() set rightLoading(loading: boolean) {
        this.rightLoading$.next(loading);
    }

    @Input() invalid = false;
    @Input() isDisabled = false;

    constructor(cd: ChangeDetectorRef) {
        super(cd);
    }

    public ngOnInit(): void {
        this.initStateChangeListener();
        this.initItemLists();
        this.initFilteredItemsStreams();
        this.initMoveButtons();
    }

    @HostListener('document:keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
        if (e.key === 'Shift') {
            this.shiftPressed = true;
        }
    }

    @HostListener('document:keyup', ['$event']) onKeyUp(e: KeyboardEvent) {
        if (e.key === 'Shift') {
            this.shiftPressed = false;
        }
    }

    @HostListener('mousedown', ['$event']) onMousedown(e: MouseEvent) {
        // Prevent text-selection on SHIFT + click
        if (e.shiftKey || e.ctrlKey || e.metaKey) {
            e.preventDefault();
        }
    }

    private initStateChangeListener(): void {
        this.rightItems$
            .pipe(
                distinctUntilChanged(),
                map(items => items.map(item => item.value)),
                shareReplay(1),
                takeUntil(this.destroy$)
            )
            .subscribe(state => this.propagateChange(state));
    }

    private initItemLists(): void {
        this.leftItems$ = combineLatest([
            this.allLeftItems$.pipe(startWith(this.allLeftItems$.value)),
            this.rightItems$.pipe(startWith(this.rightItems$.value))
        ]).pipe(
            map(([allLeftItems, rightItems]) =>
                allLeftItems.filter(leftItem => !rightItems.find(rightItem => leftItem.value === rightItem.value))
            ),
            shareReplay(1)
        );
    }

    private initFilteredItemsStreams(): void {
        this.filteredLeftItems$ = combineLatest([
            this.leftSearchControl.valueChanges.pipe(startWith('')),
            this.leftItems$
        ]).pipe(
            map(([searchTerm, leftItems]) =>
                leftItems.filter(item => item.label.toLowerCase().includes(searchTerm.toLowerCase()))
            ),
            shareReplay(1)
        );
        this.filteredLeftItems$
            .pipe(
                map(items => items.length),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe(() => (this.lastLeftIndex = -1));

        this.filteredRightItems$ = combineLatest([
            this.rightSearchControl.valueChanges.pipe(startWith('')),
            this.rightItems$
        ]).pipe(
            map(([searchTerm, rightItems]) =>
                rightItems.filter(item => item.label.toLowerCase().includes(searchTerm.toLowerCase()))
            ),
            shareReplay(1)
        );
        this.filteredRightItems$
            .pipe(
                map(items => items.length),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe(() => (this.lastRightIndex = -1));
    }

    private initMoveButtons(): void {
        const rightActionItem: Observable<ButtonActionItem> = this.hasLeftSelected.pipe(
            map(anySelected => ({
                eventListener: this.onRightClicked.bind(this),
                label: this.i18n.rightButtonLabel ?? '',
                icon: 'arrow-right',
                style: ActionItemStyle.PureSquareIconButtonLight,
                disabled: !anySelected,
                disabledInfoMsg: this.i18n.buttonDisabledMsg
            }))
        );
        const leftActionItem: Observable<ButtonActionItem> = this.hasRightSelected.pipe(
            map(anySelected => ({
                eventListener: this.onLeftClicked.bind(this),
                label: this.i18n.leftButtonLabel ?? '',
                icon: 'arrow-left',
                style: ActionItemStyle.PureSquareIconButtonLight,
                disabled: !anySelected,
                disabledInfoMsg: this.i18n.buttonDisabledMsg
            }))
        );

        this.moveActionItems$ = combineLatest([rightActionItem, leftActionItem]);
    }

    public writeValue(value: any): void {
        this.baseWriteValue(value);
    }

    private baseWriteValue(value: string[]) {
        if (!value) return;

        if (!Array.isArray(value) || !value.length) {
            this.setEmptyValue();
            return;
        }

        const arrayValue: any[] = value;

        this.initialSelectedOptions$.pipe(takeUntil(this.destroy$)).subscribe(initialSelectedOptions => {
            const parsedValues: AutocompleteFormSelectOption[] = this.parseValues(initialSelectedOptions, arrayValue);
            this.rightItems$.next(parsedValues);
        });
    }

    private setEmptyValue(): void {
        this.rightItems$.next([]);
    }

    private parseValues(options: FormSelectOption[], values: string[]): AutocompleteFormSelectOption[] {
        return values.map(item => {
            const option = options.find(option => option.value === item);
            if (!option) {
                console.warn(
                    `[ShFormDualListBaseComponent] Value '${item} is not present in the provided options: [${options.join(
                        ', '
                    )}]`
                );
                return { label: item, value: item, checked: false };
            }

            return { ...option, checked: false };
        });
    }

    public onSelectLeft(selectItem: AutocompleteFormSelectOption, index: number): void {
        if (this.isDisabled || selectItem.disabled) {
            return;
        }

        if (this.shiftPressed && this.lastLeftIndex >= 0) {
            this.selectBatch(
                index,
                this.lastLeftIndex,
                this.filteredLeftItems$,
                this.allLeftItems$,
                !selectItem.checked
            );
        } else {
            this.isSingleClick = true;
            setTimeout(() => {
                if (this.isSingleClick) {
                    this.selectSingle(selectItem, this.allLeftItems$);
                }
            }, 0);
        }
        this.lastLeftIndex = index;
    }

    public onSelectRight(selectItem: AutocompleteFormSelectOption, index: number): void {
        if (this.isDisabled || selectItem.disabled) {
            return;
        }

        if (this.shiftPressed && this.lastRightIndex >= 0) {
            this.selectBatch(
                index,
                this.lastRightIndex,
                this.filteredRightItems$,
                this.rightItems$,
                !selectItem.checked
            );
        } else {
            this.isSingleClick = true;
            setTimeout(() => {
                if (this.isSingleClick) {
                    this.selectSingle(selectItem, this.rightItems$);
                }
            }, 0);
        }
        this.lastRightIndex = index;
    }

    private selectSingle(
        selectItem: AutocompleteFormSelectOption,
        streamToUpdate$: BehaviorSubject<AutocompleteFormSelectOption[]>
    ): void {
        const newItem = { ...selectItem, checked: !selectItem.checked };
        streamToUpdate$.next(streamToUpdate$.value.map(item => (item.value === selectItem.value ? newItem : item)));
    }

    private selectBatch(
        index: number,
        prevIndex: number,
        filteredSteam$: Observable<AutocompleteFormSelectOption[]>,
        streamToUpdate$: BehaviorSubject<AutocompleteFormSelectOption[]>,
        state: boolean
    ): void {
        const start: number = Math.min(index, prevIndex);
        const end: number = Math.max(index, prevIndex) + 1;

        filteredSteam$
            .pipe(
                take(1),
                map(filteredOptions => filteredOptions.slice(start, end).filter(option => !option.disabled)),
                takeUntil(this.destroy$)
            )
            .subscribe(filteredOptions => {
                this.setStateForIntersection(streamToUpdate$, filteredOptions, state);
            });
    }

    /**
     * Sets the state of all items in streamToUpdate that also appear in filteredOptions to {state}
     * @param streamToUpdate$
     * @param filteredOptions
     * @param state
     */
    private setStateForIntersection(
        streamToUpdate$: BehaviorSubject<AutocompleteFormSelectOption[]>,
        filteredOptions: AutocompleteFormSelectOption[],
        state: boolean
    ): void {
        streamToUpdate$.next(
            streamToUpdate$.value.map(item =>
                filteredOptions.find(filterItem => filterItem.value === item.value) ? { ...item, checked: state } : item
            )
        );
    }

    public onMoveOneLeft(selectItem: AutocompleteFormSelectOption): void {
        if (this.isDisabled || selectItem.disabled) {
            return;
        }

        this.isSingleClick = false;

        const filteredItems: AutocompleteFormSelectOption[] = this.rightItems$.value.filter(
            item => item.value !== selectItem.value
        );
        this.rightItems$.next(filteredItems);
        this.stateChangeEvent([selectItem], FormValueState.UNSET);
    }

    public onMoveOneRight(selectItem: AutocompleteFormSelectOption): void {
        if (this.isDisabled || selectItem.disabled) {
            return;
        }

        this.isSingleClick = false;

        const filteredItems: AutocompleteFormSelectOption[] = this.allLeftItems$.value
            .filter(item => item.value === selectItem.value)
            .map(item => ({ ...item, checked: false }));
        const newRightItems = this.rightItems$.value.concat(filteredItems);

        this.rightItems$.next(newRightItems);
        this.allLeftItems$.next(
            this.allLeftItems$.value.map(item => (selectItem.value === item.value ? { ...item, checked: false } : item))
        );
        this.stateChangeEvent([selectItem], FormValueState.SET);
    }

    public onLeftClicked(): void {
        const [filteredItems, removedItems] = splitFilter(this.rightItems$.value, item => !item.checked);
        this.rightItems$.next(filteredItems);
        this.stateChangeEvent(removedItems, FormValueState.UNSET);
    }

    public onRightClicked(): void {
        const filteredItems: AutocompleteFormSelectOption[] = this.allLeftItems$.value
            .filter(item => item.checked)
            .map(item => ({ ...item, checked: false }));
        const newRightItems = this.rightItems$.value.concat(filteredItems);

        this.rightItems$.next(newRightItems);
        this.allLeftItems$.next(
            this.allLeftItems$.value.map(item => (item.checked ? { ...item, checked: false } : item))
        );
        this.stateChangeEvent(filteredItems, FormValueState.SET);
    }

    private stateChangeEvent(options: AutocompleteFormSelectOption[], state: FormValueState): void {
        const newState: FormValue<string>[] = this.state$.value;
        options.forEach(option => {
            const index = newState.findIndex(stateValue => stateValue.value === option.value);
            if (index >= 0) {
                newState.splice(index, 1);
            } else {
                newState.push(new FormValue<string>(option.value, state));
            }
        });
        this.state$.next(newState);
    }

    public get hasLeftSelected(): Observable<boolean> {
        return this.leftItems$.pipe(map(leftItems => leftItems.some(leftItem => leftItem.checked)));
    }

    public get hasRightSelected(): Observable<boolean> {
        return this.rightItems$.pipe(map(rightItems => rightItems.some(rightItem => rightItem.checked)));
    }

    public get hasHeaders(): boolean {
        return !!this.leftHeaderText || !!this.rightHeaderText;
    }
}
